import React, {MutableRefObject, useContext, useEffect, useMemo, useState} from 'react';
import {useTypedFastStorage} from '../../../../../hooks/useFastStorage';
import InlineInputControlRow from "../../../../../ui/layout/InlineInputControlRow";
import DateControl from "../../../../../ui/Date";
import LeftRightSplit from "../../../../../ui/LeftRightSplit";
import BranchDropDown from "../../../../../uiControls/BranchDropDown";
import LabeledControl from "../../../../../ui/LabeledControl";
import SecondaryButton from "../../../../../ui/SecondaryButton";
import {compareIso, formatDate, getDate, now} from '../../../../../dateUtils';
import Fieldset from '../../../../common/Fieldset';
import {IGeneralWithList} from "../../../../../ifmsTypes";
import TextBox from "../../../../../ui/TextBox";
import ApplicationContext, { IApplicationContext } from '../../../../../ApplicationContext';
import moment from 'moment';
import useReportViewer from '../../../../../hooks/useReportViewer';
import useGlobalOkDialog from '../../../../../hooks/useGlobalOkDialog';
import { JournalVoucherArgs } from '../../../../../fms.report/args';
import { DocumentType } from '../../../../../framework.core/DocumentType';
import { getFmsGlobal } from '../../../../../globals';

interface JournalVoucherStorageKeys {
  modelStorage: string,
};

interface JournalVoucherViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
};

interface JournalVoucherLoadedDetailsViewParams {
  journalVoucher: IGeneralWithList,
  journalVoucherRef: MutableRefObject<IGeneralWithList | null>,
  setJournalVoucher: (x: Partial<IGeneralWithList>) => void,
  onPopupClose?: () => void,
  appContext: IApplicationContext,
};

function getJournalVoucherStorageKeys(): JournalVoucherStorageKeys {
  return {modelStorage: 'report-general-ledger'}
}

function JournalVoucherImpl(params: JournalVoucherViewParams) {
  const {modelStorage} = getJournalVoucherStorageKeys();
  const [inStorage, setInStorage, removeJournalVoucherFromStorage, journalVoucherRef] = useTypedFastStorage<IGeneralWithList>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);
  const appContext = useContext(ApplicationContext);
  const fmsGlobal = getFmsGlobal();
  const noBranchSelection = (appContext.systemSettings?.enableBranch && fmsGlobal.NoBranchSelection) ?? false;

  useEffect(() => {
      initialize();
      return () => { removeJournalVoucherFromStorage() }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    return {fromDate: now(), toDate: now(), branch: noBranchSelection ? appContext.systemSettings?.userBranch ?? '' : 'ALL'}
  }

  async function initialize() {
    if (!inStorage) {
      const data = await loadExisting();
      setInStorage({...data})
    }
    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  return (
    <JournalVoucherInitializedImpl
      journalVoucher={inStorage}
      journalVoucherRef={journalVoucherRef}
      setJournalVoucher={setInStorage}
      onPopupClose={params.onPopupClose}
      appContext={appContext}
    />
  );
}

function JournalVoucherInitializedImpl({
                                      journalVoucher,
                                      journalVoucherRef,
                                      setJournalVoucher,
                                      onPopupClose,
                                      appContext,
                                    }: JournalVoucherLoadedDetailsViewParams) {
  const acctOptions = appContext.suntekSettings?.accounting;
  const startDate = acctOptions?.accountingStartDateYear && acctOptions?.accountingStartDateMonth ? moment(new Date(
    Number(acctOptions?.accountingStartDateYear),
    Number(acctOptions?.accountingStartDateMonth)-1, 1)).format() : moment(new Date(2000, 0, 1)).format();
  const {viewReport} = useReportViewer();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  function onChange(change: Partial<IGeneralWithList>) {
    setJournalVoucher(change);
  }
  
  const seqNoControl = useMemo(() =>{
    const fromDate = getDate(journalVoucher.fromDate)??"";
    const toDate = getDate(journalVoucher.toDate)??"";
    
    if(compareIso(fromDate, toDate) === 0){
      return <div className='mt-2'>
        <InlineInputControlRow mode='w210' label='Seq. #' underline alignRight>
          <LeftRightSplit
            leftSize="80px"
            rightSize='140px'
            left={
              <TextBox
                value={journalVoucher.fromSeq}
                onChange={e => onChange({fromSeq: e.target.value, toSeq: e.target.value})}
              />
            }
            right={
              <div style={{marginLeft: 30}}>
                <LabeledControl
                  label='~'
                  labelSize='30px'
                  control={
                    <TextBox
                      value={journalVoucher.toSeq}
                      onChange={e => onChange({toSeq: e.target.value})}
                    />
                  }
                />
              </div>
            }
          />
        </InlineInputControlRow>
      </div>
    }else{
      onChange({fromSeq: '', toSeq: ''})
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[journalVoucher.fromDate, journalVoucher.toDate, journalVoucher.fromSeq, journalVoucher.toSeq])

  async function print(){
  //#region CheckPrintOptions
  if(!journalVoucher.fromDate || !journalVoucher.toDate){
    await showGlobalOkDialog(<p>{"Please enter valid date."}</p>, false, null, "warning");
    return;
  }

  let errorMsg = "";
  const fromDate = getDate(journalVoucher.fromDate)??"";
  const toDate = getDate(journalVoucher.toDate)??"";
  const actStartDate = getDate(startDate)??"";

  // if(actStartDate > fromDate){
  //   errorMsg = `The starting date of Period is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)}`;
  // }
  // else if(actStartDate > toDate){
  //   errorMsg = `The ending date of Period is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)}`;
  // }
  // else 
  if(fromDate > toDate){
    errorMsg = "The ending date is smaller than the starting date.";
  }

  if(errorMsg){
    await showGlobalOkDialog(<p>{errorMsg}</p>, false, null, "warning");
    return;
  }

  //#endregion

  //PrintFunc
  const title = `Journal Voucher: ${moment(journalVoucher.fromDate).format('MM-DD-YYYY')} ~ ${moment(journalVoucher.toDate).format('MM-DD-YYYY')}`;
  const args = new JournalVoucherArgs({
    docType: DocumentType.JournalBook,
    formCaption: title,
    subject: title,
    
    stDate: fromDate,
    edDate: toDate,
    branch: journalVoucher.branch === "ALL" ? "" : journalVoucher.branch,
    slipType: 'MD',
    fromSeqNo: journalVoucher.fromSeq,
    toSeqNo: journalVoucher.toSeq,
    language: appContext.systemSettings?.primaryCurrency||'',
  });
  viewReport(args, "", true);
  }

  return (
    <div className='report-popup'>
      <Fieldset label='Period'>
        <InlineInputControlRow mode='w210' label='From' underline alignRight>
          <LeftRightSplit
            leftSize="80px"
            rightSize='140px'
            left={
              <DateControl
                name='journal-voucher-fromDate'
                value={journalVoucher.fromDate}
                onChange={fromDate => onChange({fromDate})}
              />
            }
            right={
              <div style={{marginLeft: 30}}>
                <LabeledControl
                  label='To'
                  labelSize='30px'
                  control={
                    <DateControl
                      name='journal-voucher-toDate'
                      value={journalVoucher.toDate}
                      onChange={toDate => onChange({toDate})}
                    />
                  }
                />
              </div>
            }
          />
        </InlineInputControlRow>
      </Fieldset>
      {seqNoControl}
      {/* <div className='mt-2'>
        <InlineInputControlRow mode='w210' label='Seq. No' underline alignRight>
          <LeftRightSplit
            leftSize="80px"
            rightSize='140px'
            left={
              <TextBox
                value={journalVoucher.fromSeq}
                onChange={e => onChange({fromSeq: e.target.value})}
              />
            }
            right={
              <div style={{marginLeft: 30}}>
                <LabeledControl
                  label='~'
                  labelSize='30px'
                  control={
                    <TextBox
                      value={journalVoucher.toSeq}
                      onChange={e => onChange({toSeq: e.target.value})}
                    />
                  }
                />
              </div>
            }
          />
        </InlineInputControlRow>
      </div> */}
      {appContext.suntekSettings?.company?.enableBranch ?
        <Fieldset label="Report Option">
          <InlineInputControlRow mode='w210' label='Branch' alignRight>
            <div style={{width: 80}}>
              <BranchDropDown
                isReport
                addAll
                disabled={false}
                value={journalVoucher?.branch}
                onChange={branch => onChange({branch})}
              />
            </div>
          </InlineInputControlRow>
        </Fieldset> : null}
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          onClick={() => {
            print();
          }}>
          PREVIEW
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const JournalVoucherDetails = ({
                              onPopupClose,
                            }
                              : {
  onPopupClose?: () => void,
}) => {
  // const { id } = useParams<JournalVoucherViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <JournalVoucherImpl id={''} onPopupClose={onPopupClose} key={`journalVoucher-details`}/>
  );
};
export default JournalVoucherDetails;
